/**
 * O estudo "Por que consórcio" na tela: o link público (EstudoPublicoPage) em
 * slides (EstudoSlides), com a barra do topo (cliente, baixar PDF) e o
 * WhatsApp do especialista. O editor do vendedor (PorQueConsorcioDialog) usa
 * EstudoSecoes: as mesmas seções, uma embaixo da outra, com os mesmos
 * destaques e dicas.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Download, Loader2, MessageCircle } from 'lucide-react';
import { COR, CONTEUDO_CELULAR, CONTEUDO_TELA, FONTE_PDF, montarPaginas } from './estudoPaginas';
import { baixarEstudoPdf, carregarFonte } from './estudoPdf';
import { ESTILO_INTERATIVO, ligarInteracoes } from './estudoInterativo';
import { EstudoSlides } from './EstudoSlides';
import type { Estudo } from './estudo';
import type { UserInfo } from './proposal-utils';

/** WhatsApp do especialista: número com DDD vira wa.me/55…; fora disso, sem botão. */
export const whatsappDe = (telefone: string | null | undefined): string | null => {
  const d = (telefone ?? '').replace(/\D/g, '');
  if (d.length === 10 || d.length === 11) return `https://wa.me/55${d}`;
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) return `https://wa.me/${d}`;
  return null;
};

/** Abaixo disto a apresentação usa a versão de celular (CONTEUDO_CELULAR, menos texto). */
export const LARGURA_CELULAR = 640;

/**
 * Zoom do conteúdo para a largura disponível: no monitor a página de 760 cresce
 * até 1,45×; no celular a versão de 360 ocupa a largura, com uma margem pequena.
 */
export const zoomDaLargura = (largura: number) => {
  if (largura < LARGURA_CELULAR) return Math.min(1.4, Math.max(0.8, (largura - 24) / CONTEUDO_CELULAR));
  return Math.min(1.45, Math.max(0.3, (largura - 96) / CONTEUDO_TELA));
};

const useZoom = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [celular, setCelular] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => {
      const largura = el.clientWidth || window.innerWidth;
      setZoom(zoomDaLargura(largura));
      setCelular(largura < LARGURA_CELULAR);
    };
    medir();
    const obs = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    obs?.observe(el);
    window.addEventListener('resize', medir);
    return () => {
      obs?.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, []);
  return { ref, zoom, celular };
};

/**
 * Só as seções do estudo, no zoom da largura do próprio espaço. A tela do
 * link e a pré-visualização do editor (PorQueConsorcioDialog) usam esta parte.
 */
export const EstudoSecoes = ({ estudo, especialista }: { estudo: Estudo; especialista: UserInfo }) => {
  const { ref, zoom, celular } = useZoom();

  // Montserrat, a fonte do logo: entra quando carregar, sem segurar a tela.
  useEffect(() => {
    void carregarFonte();
  }, []);
  const secoes = useMemo(() => {
    try {
      return montarPaginas(estudo, especialista, window.location.origin, celular ? 'celular' : 'tela');
    } catch (err) {
      console.error('Estudo: falha ao montar as páginas', err);
      return null;
    }
  }, [estudo, especialista, celular]);

  const main = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = main.current;
    if (!el) return;
    return ligarInteracoes(el);
  }, [secoes]);

  return (
    <div ref={ref} className="w-full">
      <style>{ESTILO_INTERATIVO}</style>
      {secoes === null ? (
        <p className="px-6 py-24 text-center text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
          Não foi possível montar este estudo.
        </p>
      ) : (
        <main ref={main} style={{ ['--estudo-zoom' as string]: zoom }}>
          {secoes.map((html, i) => (
            <div key={i} dangerouslySetInnerHTML={{ __html: html }} />
          ))}
        </main>
      )}
    </div>
  );
};

type Props = {
  estudo: Estudo;
  especialista: UserInfo;
  /** Linha extra no rodapé (ex.: validade do link). */
  rodape?: ReactNode;
  /** Botões extras à direita da barra do topo (ex.: fechar a pré-visualização). */
  acoes?: ReactNode;
  /** Faixa acima do nome do cliente na barra do topo. */
  selo?: string;
};

export const EstudoTela = ({ estudo, especialista, rodape, acoes, selo = 'Apresentação de consórcio' }: Props) => {
  const [baixando, setBaixando] = useState(false);

  const baixar = async () => {
    setBaixando(true);
    try {
      await baixarEstudoPdf(estudo, especialista);
    } catch (err) {
      console.error('Estudo: falha ao gerar o PDF', err);
    } finally {
      setBaixando(false);
    }
  };

  const whatsapp = whatsappDe(especialista.phone);
  const botao =
    'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60';

  return (
    <div className="flex h-[100dvh] w-full flex-col overflow-hidden" style={{ background: COR.noite, fontFamily: FONTE_PDF, color: '#fff' }}>
      <header
        className="z-10 flex-none border-b backdrop-blur"
        style={{ background: 'rgba(15,26,36,0.88)', borderColor: 'rgba(196,173,142,0.25)' }}
      >
        <div className="flex items-center gap-3 px-4 py-3 sm:px-8">
          <img src="/logos/logo-mark-white.png" alt="North" className="h-8 w-8 shrink-0 object-contain" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: COR.ouro }}>
              {selo}
            </div>
            <div className="truncate text-sm font-bold">{estudo.cliente ?? 'Apresentação personalizada'}</div>
          </div>
          {whatsapp && (
            <a
              href={whatsapp}
              target="_blank"
              rel="noreferrer"
              className={botao.replace('inline-flex', 'hidden sm:inline-flex')}
              style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}
            >
              <MessageCircle className="h-4 w-4" /> Falar com o especialista
            </a>
          )}
          <button type="button" onClick={baixar} disabled={baixando} className={botao} style={{ background: COR.ouro, color: COR.marinho }}>
            {baixando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="hidden sm:inline">Baixar PDF</span>
            <span className="sm:hidden">PDF</span>
          </button>
          {acoes}
        </div>
      </header>

      <EstudoSlides
        estudo={estudo}
        especialista={especialista}
        fim={
          <footer className="px-4 pb-20 pt-4 text-center text-xs sm:pb-6" style={{ color: COR.suave }}>
            Apresentação com taxas e premissas do dia em que foi gerada; não é garantia de rentabilidade nem de contemplação.
            {rodape}
          </footer>
        }
      />

      {whatsapp && (
        <a
          href={whatsapp}
          target="_blank"
          rel="noreferrer"
          className={`${botao} fixed inset-x-4 bottom-16 z-10 justify-center py-3 sm:hidden`}
          style={{ background: COR.verde, color: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}
        >
          <MessageCircle className="h-4 w-4" /> Falar com o especialista
        </a>
      )}
    </div>
  );
};
